import { MetricCard, type MetricCardAction } from './metric-card';
import { TICKET_METRIC_KEYS } from '@/types/analyticsV2';
import type { ScalarMetricResult } from '@/types/analyticsV2';
import { useMetricsPerKey } from '@/hooks/use-analytics-v2';
import { TicketDrilldown } from '../drilldown/ticket-drilldown';
import { ticketDrilldownView, useTicketDrilldown, type TicketDrilldownKey } from '../drilldown/ticket-drilldowns';

interface TicketMetricsSectionProps {
  fromDate: string;
  toDate: string;
  /**
   * Human-readable label of the previous period the trend is computed
   * against. Passed to each card so the trend pill tooltip can show
   * "vs Mar 2 to Mar 31".
   */
  comparisonLabel?: string;
  /** Super-admin only: render metrics for this account instead of the caller's own. */
  overrideAccountId?: number;
}

/**
 * Round a numeric value to one decimal, returning null if the input is null.
 * Used for the "Avg AI responses /ticket" card where the percent field on
 * the scalar carries a plain ratio (e.g. 1.4), not a percentage.
 */
const round1 = (n: number | null | undefined): number | null =>
  n === null || n === undefined ? null : Number(n.toFixed(1));

/**
 * Four-card Overview row for the Ticket Metrics tab. Three of the cards
 * consume existing scalars in display-specific ways; the fourth is a
 * placeholder for the Productivity Gain metric whose formula has not been
 * defined yet. The placeholder ships with the rest so the layout matches
 * the Figma now and only the value needs to fill in later.
 */
export function TicketMetricsSection({ fromDate, toDate, comparisonLabel, overrideAccountId }: TicketMetricsSectionProps) {
  const results = useMetricsPerKey(TICKET_METRIC_KEYS, fromDate, toDate, overrideAccountId);
  const byKey = Object.fromEntries(results.map((r) => [r.key, r]));

  const coverage = byKey['aiTicketCoverage'];
  const avgResp = byKey['avgAiResponsesPerTicket'];
  const automation = byKey['aiAutomation'];

  const data = (r: typeof coverage): ScalarMetricResult | undefined => r?.data;
  const { show, ...drilldown } = useTicketDrilldown();
  const opens = (key: TicketDrilldownKey, label: string): MetricCardAction => ({
    label,
    onOpen: () => show(ticketDrilldownView(key)),
  });

  return (
    <div className='flex flex-col gap-3'>
      <h2 className='text-[12px] text-gray-800 font-medium'>Overview</h2>
      <div className='overflow-x-auto'>
        <div className='grid grid-cols-4 gap-3 auto-cols-[minmax(260px,1fr)] min-w-[1100px]'>
          <MetricCard
            title='Tickets Handled by AI'
            description='Conversations where AI helped at any point.'
            value={data(coverage)?.current.count ?? null}
            denominator={data(coverage)?.current.total ?? null}
            trend={data(coverage)?.trend.countChangePct ?? null}
            comparisonLabel={comparisonLabel}
            isLoading={coverage?.isLoading ?? false}
            valueAction={opens('aiHandled', 'Open the tickets handled by AI')}
            denominatorAction={opens('tickets', 'Open all tickets')}
          />
          <MetricCard
            title='Avg AI responses /ticket'
            description='Avg AI responses per conversation in the period.'
            value={round1(data(avgResp)?.current.percent ?? null)}
            trend={data(avgResp)?.trend.percentPointChange ?? null}
            comparisonLabel={comparisonLabel}
            isLoading={avgResp?.isLoading ?? false}
            valueAction={opens('avgAiResponses', 'Open the tickets with their AI responses')}
          />
          <MetricCard
            title='Automation Rate'
            description='Share of conversations where AI auto-replied at least once.'
            value={data(automation)?.current.percent ?? null}
            unit='%'
            trend={data(automation)?.trend.percentPointChange ?? null}
            comparisonLabel={comparisonLabel}
            isLoading={automation?.isLoading ?? false}
            valueAction={opens('automated', 'Open the automated tickets')}
          />
          <MetricCard
            title='Productivity Gain'
            description='Time saved per ticket vs the pre-AI baseline. Coming soon.'
            value={null}
          />
        </div>
      </div>
      <TicketDrilldown {...drilldown} fromDate={fromDate} toDate={toDate} overrideAccountId={overrideAccountId} />
    </div>
  );
}
